import React, { useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import PermissionForm from '../components/PermissionForm';
import PermissionMatrix from '../components/PermissionMatrix';
import RoleForm from '../components/RoleForm';
import { useAccessControl } from '../hooks/useAccessControl';
import type { Permission } from '../types';

const AdminSettingsPage: React.FC = () => {
  const { t } = useTranslation();
  const [editingPermission, setEditingPermission] = useState<Permission | null>(null);
  const permissionFormRef = useRef<HTMLDivElement>(null);
  const {
    permissions,
    roles,
    rolePermissionDrafts,
    dirtyRoleIds,
    isLoading,
    hasLoaded,
    loadError,
    pendingAction,
    loadAccessControl,
    toggleRolePermission,
    saveRolePermissions,
    savePermission,
    removePermission,
    addRole,
  } = useAccessControl();

  const startEditingPermission = (permission: Permission) => {
    setEditingPermission(permission);
    window.requestAnimationFrame(() => {
      permissionFormRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' });
    });
  };

  const submitPermission = async (input: { name: string; description: string }) => {
    const didSave = await savePermission(input, editingPermission);
    if (didSave) setEditingPermission(null);
    return didSave;
  };

  return (
    <div className="mx-auto flex min-h-full w-full min-w-0 max-w-[1600px] flex-col gap-md sm:gap-lg">
      <header className="flex flex-col gap-3 sm:flex-row sm:items-end sm:justify-between">
        <div>
          <h1 className="font-h1 text-h1 text-on-surface">{t('admin.settings.title')}</h1>
          <p className="mt-1 text-body-md text-secondary">{t('admin.settings.subtitle')}</p>
        </div>
        <button
          type="button"
          className="inline-flex min-h-11 w-full items-center justify-center gap-2 rounded-xl border border-outline-variant bg-white px-4 font-label-md text-secondary shadow-sm transition-colors hover:bg-surface-container-low focus:outline-none focus:ring-2 focus:ring-primary/20 disabled:cursor-not-allowed disabled:opacity-60 sm:w-auto"
          disabled={isLoading || pendingAction !== null}
          onClick={() => void loadAccessControl()}
        >
          <span className={`material-symbols-outlined text-[19px] ${isLoading ? 'animate-spin' : ''}`}>refresh</span>
          {t('admin.settings.actions.refresh')}
        </button>
      </header>

      <section className="relative min-w-0 overflow-hidden rounded-[20px] border border-outline-variant/70 bg-white shadow-sm">
        {isLoading && hasLoaded && (
          <div className="absolute inset-x-0 top-0 z-40 h-0.5 overflow-hidden bg-primary-fixed" role="status" aria-label={t('admin.settings.loading')}>
            <div className="h-full w-1/2 animate-pulse bg-primary" />
          </div>
        )}
        <div className="flex flex-col gap-3 border-b border-outline-variant bg-white px-4 py-4 sm:flex-row sm:items-center sm:justify-between sm:px-5">
          <div className="min-w-0">
            <h2 className="font-h2 text-h2 text-on-surface">{t('admin.settings.matrix.title')}</h2>
            <p className="mt-1 text-body-sm text-secondary">{t('admin.settings.matrix.description')}</p>
          </div>
          <div className="flex items-center gap-2 rounded-full bg-surface-container-low px-3 py-1.5 text-body-sm text-secondary">
            <span className="material-symbols-outlined text-[18px] text-primary">shield_person</span>
            <span>{t('admin.settings.matrix.summary', {
              permissionCount: permissions.length,
              roleCount: roles.length,
            })}</span>
          </div>
        </div>

        {!hasLoaded && isLoading ? (
          <div className="space-y-3 p-4 sm:p-5" role="status" aria-label={t('admin.settings.loading')}>
            {Array.from({ length: 7 }, (_, index) => (
              <div key={index} className="h-12 animate-pulse rounded-xl bg-surface-container-low" />
            ))}
          </div>
        ) : loadError && permissions.length === 0 ? (
          <div className="flex min-h-[300px] flex-col items-center justify-center gap-3 px-4 text-center" role="alert">
            <span className="material-symbols-outlined text-4xl text-error">cloud_off</span>
            <div>
              <p className="font-medium text-on-surface">{t('admin.settings.load_error')}</p>
              <p className="mt-1 text-body-sm text-error">{loadError}</p>
            </div>
            <button
              type="button"
              className="min-h-10 rounded-xl bg-primary px-4 font-label-md text-on-primary hover:bg-on-primary-fixed-variant"
              onClick={() => void loadAccessControl()}
            >
              {t('admin.settings.actions.retry')}
            </button>
          </div>
        ) : (
          <>
            {loadError && (
              <div className="flex flex-wrap items-center justify-between gap-3 border-b border-error/20 bg-error-container/35 px-4 py-2 text-body-sm text-error" role="alert">
                <span>{loadError}</span>
                <button type="button" className="font-label-sm text-primary hover:underline" onClick={() => void loadAccessControl()}>{t('admin.settings.actions.retry')}</button>
              </div>
            )}
            <PermissionMatrix
              permissions={permissions}
              roles={roles}
              rolePermissionDrafts={rolePermissionDrafts}
              dirtyRoleIds={dirtyRoleIds}
              isRefreshing={isLoading}
              pendingAction={pendingAction}
              onEditPermission={startEditingPermission}
              onDeletePermission={async (permission) => {
                const didDelete = await removePermission(permission);
                if (didDelete && editingPermission?.id === permission.id) setEditingPermission(null);
                return didDelete;
              }}
              onTogglePermission={toggleRolePermission}
              onSaveRole={(role) => void saveRolePermissions(role)}
            />
          </>
        )}
      </section>

      <div className="grid grid-cols-1 gap-md xl:grid-cols-2">
        <div ref={permissionFormRef}>
          <PermissionForm
            editingPermission={editingPermission}
            isSubmitting={pendingAction === 'permission'}
            isDisabled={pendingAction !== null}
            onSubmit={submitPermission}
            onCancelEdit={() => setEditingPermission(null)}
          />
        </div>
        <RoleForm
          permissions={permissions}
          isSubmitting={pendingAction === 'role'}
          isDisabled={pendingAction !== null}
          onSubmit={addRole}
        />
      </div>
    </div>
  );
};

export default AdminSettingsPage;
